import Vue from 'vue';

export const TAG_MARKER_OUTCOME = Object.freeze({
  CONFIRMED_SUCCESS: 'confirmed-success',
  CONFIRMED_FAILURE: 'confirmed-failure',
  WRITE_SUCCEEDED_REFRESH_FAILED: 'write-succeeded-refresh-failed',
  AMBIGUOUS: 'ambiguous',
});

export const TAG_MARKER_METADATA_FIELDS = Object.freeze([
  'name', 'type', 'category', 'thumbnailUrl',
  'level2D', 'condition', 'pinColor', 'state',
]);

const OUTCOMES = new Set(Object.values(TAG_MARKER_OUTCOME));
const ROOT_MARKER_ARRAYS = Object.freeze([
  'tags',
  'annotations',
  'scaffolds',
  'inspections',
]);
const TAGS_MARKER_ARRAYS = Object.freeze([
  'automaticTags',
  'allExistingAutomaticDbids',
  'manualStructuralTags',
  'structuralTags',
  'allExistingStructuralDbIds',
  'manualPipelinesTags',
  'pipelinesTags',
  'allExistingPipelineDbIds',
  'allExistingAnomaliesDbIds',
  'filteredTags',
  'amisTags',
  'allExistingAMISDbids',
  'avaInspectTags',
]);
const FEATURE_LIST_SOURCES = Object.freeze([
  ['EQUIPMENT_VIEW', 'entries'],
  ['PIPELINE_VIEW', 'entries'],
  ['STRUCTURAL_VIEW', 'entries'],
  ['GROUP_VIEW', 'entries'],
  ['ANOMALY_VIEW', 'entries'],
  ['OPERATION_VIEW', 'entries'],
  ['INSPECTION_VIEW', 'entries'],
  ['AVA_INSPECT', 'avaInspectTags'],
]);
const MARKER_FAMILY_BY_TOKEN = Object.freeze({
  equipment: 'equipment',
  forgeequipment: 'equipment',
  pipeline: 'pipeline',
  forgepipeline: 'pipeline',
  structural: 'structural',
  forgestructural: 'structural',
  inspection: 'inspection',
  forgeinspection: 'inspection',
  annotation: 'annotation',
  forgeannotation: 'annotation',
  general: 'annotation',
  anomaly: 'anomaly',
  forgeanomaly: 'anomaly',
  avainspect: 'ava-inspect',
  forgeavainspect: 'ava-inspect',
  scaffold: 'scaffold',
  permit: 'scaffold',
  container: 'scaffold',
  forgescaffold: 'scaffold',
});

function requiredId(value, label) {
  if (value == null) throw new TypeError(`${label} is required`);
  return String(value);
}

function idsEqual(left, right) {
  return left != null && right != null && String(left) === String(right);
}

function isFinitePoint(point) {
  return point != null
    && Number.isFinite(point.x)
    && Number.isFinite(point.y)
    && Number.isFinite(point.z);
}

function frozenPoint(point) {
  if (!isFinitePoint(point)) return null;
  return Object.freeze({ x: point.x, y: point.y, z: point.z });
}

function hasOwn(value, key) {
  return Object.prototype.hasOwnProperty.call(value, key);
}

function normalizedMarkerFamily(value) {
  if (value == null) return null;
  const token = String(value).toLowerCase().replace(/[^a-z]/g, '');
  return MARKER_FAMILY_BY_TOKEN[token] || null;
}

function markerFamilyForTag(tag) {
  const candidates = [
    typeof tag.getType === 'function' ? tag.getType() : null,
    tag.markerFamily,
    tag.family,
    tag.discriminator,
    tag.type,
  ];
  return candidates.map(normalizedMarkerFamily).find(Boolean) || 'generic';
}

export function isScaffoldMarkerPlaced(tag) {
  const position = tag?.position;
  return !!(position && (position.x || position.y || position.z));
}

export function resolveTagMarkerPoint(tag) {
  if (!tag) return null;
  const isScaffold = markerFamilyForTag(tag) === 'scaffold';
  if (isScaffold && !isScaffoldMarkerPlaced(tag)) return null;
  const point = isScaffold ? tag.position : tag.point;
  return isFinitePoint(point) ? point : null;
}

export function createTagMarkerLifecycleReceipt(input = {}) {
  const tagId = requiredId(input.tagId, 'tagId');
  const facilityId = requiredId(input.facilityId, 'facilityId');
  if (!OUTCOMES.has(input.outcome)) throw new TypeError('outcome is invalid');
  if (input.operation == null) throw new TypeError('operation is required');

  const receipt = {
    operation: String(input.operation),
    tagId,
    facilityId,
    outcome: input.outcome,
    confirmedAbsentInScope: input.outcome === TAG_MARKER_OUTCOME.CONFIRMED_SUCCESS
      && input.confirmedAbsentInScope === true,
  };
  if (input.companyId != null) receipt.companyId = String(input.companyId);
  if (input.principalId != null) receipt.principalId = String(input.principalId);
  if (hasOwn(input, 'authoritativePoint') && input.authoritativePoint !== undefined) {
    if (input.authoritativePoint !== null && !isFinitePoint(input.authoritativePoint)) {
      throw new TypeError('authoritativePoint must be null or a finite {x, y, z} point');
    }
    receipt.authoritativePoint = input.authoritativePoint === null
      ? null
      : frozenPoint(input.authoritativePoint);
  }
  return Object.freeze(receipt);
}

export function createPanoramaTagSelectionSeed({ tag, facilityId, scanId } = {}) {
  if (!tag) throw new TypeError('tag is required');
  const seed = {
    tagId: requiredId(tag.id, 'tag.id'),
    facilityId: requiredId(facilityId, 'facilityId'),
    scanId: requiredId(scanId, 'scanId'),
    markerFamily: markerFamilyForTag(tag),
    point: frozenPoint(resolveTagMarkerPoint(tag)),
    positionImage360Id: tag.positionImage360Id,
    isDeleted: tag.isDeleted === true,
  };

  TAG_MARKER_METADATA_FIELDS.forEach(field => {
    if (tag[field] !== undefined) seed[field] = tag[field];
  });
  return Object.freeze(seed);
}

export function createCatalogReconciliationReceipts({
  operation,
  tagIds,
  facilityId,
  catalog,
  authoritative,
} = {}) {
  const uniqueIds = [...new Set((Array.isArray(tagIds) ? tagIds : [tagIds])
    .filter(value => value != null)
    .map(value => String(value)))];
  const presentIds = new Set(
    (Array.isArray(catalog) ? catalog : [])
      .filter(row => row?.id != null)
      .map(row => String(row.id)),
  );

  return uniqueIds.map(tagId => {
    let outcome = TAG_MARKER_OUTCOME.AMBIGUOUS;
    let confirmedAbsentInScope = false;
    if (authoritative === true) {
      const present = presentIds.has(tagId);
      outcome = present
        ? TAG_MARKER_OUTCOME.CONFIRMED_FAILURE
        : TAG_MARKER_OUTCOME.CONFIRMED_SUCCESS;
      confirmedAbsentInScope = !present;
    }
    return createTagMarkerLifecycleReceipt({
      operation,
      tagId,
      facilityId,
      outcome,
      confirmedAbsentInScope,
    });
  });
}

export function createPanoramaTagSelectionScope({ seed, generation } = {}) {
  if (!seed) throw new TypeError('seed is required');
  if (!Number.isInteger(generation)) throw new TypeError('generation must be an integer');

  return Object.freeze({
    ...seed,
    point: frozenPoint(seed.point),
    isDeleted: seed.isDeleted === true,
    generation,
  });
}

export function canRenderSelectedPointMarker({
  selection,
  currentGeneration,
  currentFacilityId,
  currentScanId,
  receipt,
} = {}) {
  if (!selection || !Number.isInteger(currentGeneration)) return false;
  if (selection.generation !== currentGeneration) return false;
  if (!idsEqual(selection.facilityId, currentFacilityId)) return false;
  if (!idsEqual(selection.scanId, currentScanId)) return false;
  if (selection.isDeleted === true) return false;

  let point = selection.point;
  const receiptMatchesScope = receipt
    && idsEqual(receipt.tagId, selection.tagId)
    && idsEqual(receipt.facilityId, selection.facilityId);

  if (receiptMatchesScope) {
    if (receipt.outcome === TAG_MARKER_OUTCOME.WRITE_SUCCEEDED_REFRESH_FAILED) return false;
    if (receipt.outcome === TAG_MARKER_OUTCOME.CONFIRMED_SUCCESS) {
      if (receipt.confirmedAbsentInScope === true) return false;
      if (hasOwn(receipt, 'authoritativePoint')) point = receipt.authoritativePoint;
    }
  }

  return isFinitePoint(point);
}

function normalizedFacilityValues(row) {
  const values = [];
  ['facilityId', 'forgeAssetId', 'assetId'].forEach(field => {
    if (row?.[field] != null) values.push(String(row[field]));
  });
  ['positionedFacilityIds', 'otherPositionedFacilityIds'].forEach(field => {
    if (!Array.isArray(row?.[field])) return;
    row[field].forEach(value => {
      if (value != null) values.push(String(value));
    });
  });
  if (Array.isArray(row?.spatialTargets)) {
    row.spatialTargets.forEach(target => {
      if (target?.facilityId != null) values.push(String(target.facilityId));
    });
  }
  return values;
}

function isSafelyCurrentFacilityRow(row, facilityId) {
  const explicitFacilities = normalizedFacilityValues(row);
  if (explicitFacilities.length === 0) return true;
  if (!explicitFacilities.some(value => value === facilityId)) return false;
  return explicitFacilities.every(value => value === facilityId);
}

function reconcileRetainedRowCurrentAnchor(row, facilityId) {
  if (!row) return;
  const facilities = normalizedFacilityValues(row);
  if (facilities.length && !facilities.includes(facilityId)) return;
  if (Array.isArray(row.positionedFacilityIds)) {
    row.positionedFacilityIds = row.positionedFacilityIds
      .filter(value => String(value) !== facilityId);
  }
  if (Array.isArray(row.spatialTargets)) {
    row.spatialTargets = row.spatialTargets
      .filter(target => String(target?.facilityId) !== facilityId);
  }
  if ('point' in row) row.point = null;
  if ('position' in row) {
    const origin = { x: 0, y: 0, z: 0 };
    if (typeof row.position?.set === 'function') row.position.set(0, 0, 0);
    else row.position = origin;
  }
  if ('positionImage360Id' in row) row.positionImage360Id = null;
}

function removeScopedId(array, tagId, facilityId) {
  if (!Array.isArray(array)) return 0;
  let removed = 0;
  for (let index = array.length - 1; index >= 0; index -= 1) {
    const row = array[index];
    if (!idsEqual(row?.id, tagId)) continue;
    if (!isSafelyCurrentFacilityRow(row, facilityId)) {
      reconcileRetainedRowCurrentAnchor(row, facilityId);
      continue;
    }
    array.splice(index, 1);
    removed += 1;
  }
  return removed;
}

export function removeTagIdFromMarkerArrays(rootState, tagId, facilityId) {
  if (!rootState) return 0;
  const normalizedTagId = requiredId(tagId, 'tagId');
  const normalizedFacilityId = requiredId(facilityId, 'facilityId');
  if (!idsEqual(rootState.assetId, normalizedFacilityId)) return 0;

  const arrays = ROOT_MARKER_ARRAYS.map(field => rootState[field]);
  TAGS_MARKER_ARRAYS.forEach(field => arrays.push(rootState.TAGS?.[field]));
  FEATURE_LIST_SOURCES.forEach(([namespace, key]) => arrays.push(rootState[namespace]?.[key]));

  return arrays.reduce(
    (removed, array) => removed + removeScopedId(array, normalizedTagId, normalizedFacilityId),
    0,
  );
}

export function invalidateTagAnchorInMarkerArrays(rootState, tagId, facilityId) {
  if (!rootState) return 0;
  const normalizedTagId = requiredId(tagId, 'tagId');
  const normalizedFacilityId = requiredId(facilityId, 'facilityId');
  if (!idsEqual(rootState.assetId, normalizedFacilityId)) return 0;
  const arrays = ROOT_MARKER_ARRAYS.map(field => rootState[field]);
  TAGS_MARKER_ARRAYS.forEach(field => arrays.push(rootState.TAGS?.[field]));
  FEATURE_LIST_SOURCES.forEach(([namespace, key]) => arrays.push(rootState[namespace]?.[key]));
  let invalidated = 0;
  arrays.forEach(array => {
    if (!Array.isArray(array)) return;
    array.forEach(row => {
      if (!idsEqual(row?.id, normalizedTagId)) return;
      const facilities = normalizedFacilityValues(row);
      if (facilities.length && !facilities.includes(normalizedFacilityId)) return;
      reconcileRetainedRowCurrentAnchor(row, normalizedFacilityId);
      invalidated += 1;
    });
  });
  return invalidated;
}

export function upsertTagById(array, tag) {
  if (!Array.isArray(array)) throw new TypeError('array is required');
  if (!tag) throw new TypeError('tag is required');
  const tagId = requiredId(tag.id, 'tag.id');
  const index = array.findIndex(value => idsEqual(value?.id, tagId));
  if (index === -1) {
    array.push(tag);
    return 'inserted';
  }
  array.splice(index, 1, tag);
  return 'updated';
}

export function applyTagMarkerMetadataFields(target, source) {
  if (!target || !source || target.id == null || source.id == null) return false;
  if (!idsEqual(target.id, source.id)) return false;

  TAG_MARKER_METADATA_FIELDS.forEach(field => {
    if (source[field] === undefined) return;
    if (field in target) {
      target[field] = source[field];
    } else {
      Vue.set(target, field, source[field]);
    }
  });
  return true;
}
